// "Start a new Email" picker modal — opens from the Home + Email list
// "+ New Email" CTAs. Per spec/build-plan.md P-032 row deliverable
// ("'Duplicate from previous Email' workflow on Home / Email list") +
// Banks's pre-flight Ask 2 call (option (a): Home + Email-list "+ New
// Email" opens a Template-and-Email picker modal).
//
// **Three paths.** Order matches staff intent:
// 1. **From a template** — primary path for the monthly newsletter
//    flow (pick the Newsletter template, fill content, send).
// 2. **Duplicate from a previous Email** — second-most-common path
//    for "this month is a copy-edit of last month's content."
// 3. **Start blank** — escape hatch / cold-start affordance. Always
//    visible so the picker is never empty (no templates / no prior
//    emails on a brand-new install).
//
// **Built on `<Modal>` (P-032).** Per /simplify at P-032 close — this
// is the 3rd modal consumer that triggered the shared scaffold
// extraction. Layers the title + Close button + three picker sections
// on top of the shell.

import { useEffect, useRef } from 'preact/hooks'

import { sortByUpdatedDesc } from '../../lib/sort'
import {
  createAndOpenEmail,
  createAndOpenEmailFromTemplate,
  duplicateAndOpenEmail,
} from '../../store/factories'
import type { Email, Template } from '../../store/schemas'
import { secondaryButtonClassCompactSm } from '../button-classes'
import { Modal } from '../modal'

interface NewEmailModalProps {
  readonly open: boolean
  readonly templates: readonly Template[]
  readonly emails: readonly Email[]
  readonly onCancel: () => void
}

export function NewEmailModal({ open, templates, emails, onCancel }: NewEmailModalProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
  }, [open])

  const sortedTemplates = sortByUpdatedDesc(templates)
  const sortedEmails = sortByUpdatedDesc(emails)

  const handleStartBlank = () => {
    onCancel()
    createAndOpenEmail()
  }

  const handlePickTemplate = (t: Template) => {
    onCancel()
    createAndOpenEmailFromTemplate(t)
  }

  const handleDuplicateEmail = (e: Email) => {
    onCancel()
    duplicateAndOpenEmail(e)
  }

  return (
    <Modal open={open} onCancel={onCancel} labelledBy="new-email-modal-title" size="lg">
      <div class="flex items-baseline justify-between gap-4">
        <h2 id="new-email-modal-title" class="m-0 text-lg font-semibold text-gray-900">
          Start a new Email
        </h2>
        <button
          ref={closeRef}
          type="button"
          onClick={onCancel}
          class={secondaryButtonClassCompactSm}
        >
          Close
        </button>
      </div>

      <div class="mt-4 space-y-6">
          <section aria-labelledby="new-email-template">
            <h3
              id="new-email-template"
              class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              From a template
            </h3>
            {sortedTemplates.length === 0 ? (
              <p class="m-0 text-sm text-gray-600">
                No Templates yet — create one from the Templates screen first.
              </p>
            ) : (
              <ul class="m-0 list-none space-y-1 p-0">
                {sortedTemplates.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => handlePickTemplate(t)}
                      class="w-full rounded border border-gray-200 bg-white px-3 py-2 text-left hover:border-gray-300 hover:bg-gray-50"
                    >
                      <div class="text-sm font-medium text-gray-900">{t.name}</div>
                      <div class="text-xs text-gray-500">
                        {t.blocks.length} block{t.blocks.length === 1 ? '' : 's'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="new-email-duplicate">
            <h3
              id="new-email-duplicate"
              class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Duplicate from a previous Email
            </h3>
            {sortedEmails.length === 0 ? (
              <p class="m-0 text-sm text-gray-600">No previous Emails to duplicate.</p>
            ) : (
              <ul class="m-0 list-none space-y-1 p-0">
                {sortedEmails.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => handleDuplicateEmail(e)}
                      class="w-full rounded border border-gray-200 bg-white px-3 py-2 text-left hover:border-gray-300 hover:bg-gray-50"
                    >
                      <div class="text-sm font-medium text-gray-900">{e.name}</div>
                      <div class="text-xs text-gray-500">
                        {e.templateName !== '' ? e.templateName : 'No template'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="new-email-blank">
            <h3
              id="new-email-blank"
              class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Or start fresh
            </h3>
            <button
              type="button"
              onClick={handleStartBlank}
              class="w-full rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100"
            >
              Start blank — no Template, no Blocks
            </button>
          </section>
      </div>
    </Modal>
  )
}
